import type { AnyFieldApi } from "@tanstack/react-form"
import { cn } from "@/lib/utils"

interface FormFieldProps {
  children: React.ReactNode
  className?: string
}

function FormField({ children, className }: FormFieldProps) {
  return <div className={cn("space-y-1.5", className)}>{children}</div>
}

interface FormLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean
}

function FormLabel({ className, required, children, ...props }: FormLabelProps) {
  return (
    <label
      className={cn(
        "text-xs font-medium text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        className
      )}
      {...props}
    >
      {children}
      {required && <span className="ml-0.5 text-destructive">*</span>}
    </label>
  )
}

interface FormDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {}

function FormDescription({ className, ...props }: FormDescriptionProps) {
  return (
    <p
      className={cn("text-xs text-muted-foreground", className)}
      {...props}
    />
  )
}

interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  error?: string
}

function FormMessage({ className, error, children, ...props }: FormMessageProps) {
  const message = error || children

  if (!message) {
    return null
  }

  return (
    <p
      className={cn("text-xs text-destructive", className)}
      {...props}
    >
      {message}
    </p>
  )
}

interface FieldInfoProps {
  field: AnyFieldApi
}

function FieldInfo({ field }: FieldInfoProps) {
  const errors = field.state.meta.errors
  const isTouched = field.state.meta.isTouched
  const isValidating = field.state.meta.isValidating

  return (
    <>
      {isTouched && errors.length > 0 && (
        <FormMessage error={errors.join(", ")} />
      )}
      {isValidating && (
        <p className="text-xs text-muted-foreground">Validating...</p>
      )}
    </>
  )
}

export { FormField, FormLabel, FormDescription, FormMessage, FieldInfo }
